
import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [caseStudiesOpen, setCaseStudiesOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleCaseStudies = () => setCaseStudiesOpen(!caseStudiesOpen);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? "py-3 bg-secondary/80 backdrop-blur-md shadow-sm" : "py-5 bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="relative flex items-center justify-center">
          {/* Centered Logo */}
          <Link to="/" className="text-5xl md:text-6xl font-medium text-foreground">
            HeyVail
          </Link>

          {/* Desktop Navigation - positioned absolutely to the right */}
          <nav className="hidden md:flex items-center space-x-10 absolute right-0">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors link-hover">
              Features
            </a>
            <a href="#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors link-hover">
              How It Works
            </a>
            <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition-colors link-hover">
              Results
            </a>
            <div className="relative group">
              <button 
                onClick={toggleCaseStudies}
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors link-hover"
              >
                Case Studies <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div className={`absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-secondary/90 backdrop-blur-md ring-1 ring-border py-1 transition-all duration-200 ${caseStudiesOpen || 'opacity-0 invisible'}`}>
                <Link 
                  to="/case-studies/b2b-lending" 
                  className="block px-4 py-2 text-sm hover:bg-primary/10 hover:text-foreground transition-colors"
                  onClick={() => setCaseStudiesOpen(false)}
                >
                  B2B Lending
                </Link>
                <Link 
                  to="/case-studies/healthcare" 
                  className="block px-4 py-2 text-sm hover:bg-primary/10 hover:text-foreground transition-colors"
                  onClick={() => setCaseStudiesOpen(false)}
                >
                  Healthcare
                </Link>
                <Link 
                  to="/case-studies/service-business" 
                  className="block px-4 py-2 text-sm hover:bg-primary/10 hover:text-foreground transition-colors"
                  onClick={() => setCaseStudiesOpen(false)}
                >
                  Service Businesses
                </Link>
              </div>
            </div>
            <a href="#contact" className="primary-button">
              Get Started
            </a>
          </nav>

          {/* Mobile Menu Button - positioned absolutely to the right */}
          <button className="md:hidden absolute right-0" onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-background z-40 transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } md:hidden`}
      >
        <div className="flex flex-col h-full justify-center items-center space-y-8 p-8">
          <a 
            href="#features" 
            className="text-xl text-foreground font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            Features
          </a>
          <a 
            href="#how-it-works" 
            className="text-xl text-foreground font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            How It Works
          </a>
          <a 
            href="#testimonials" 
            className="text-xl text-foreground font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            Results
          </a>
          
          <div className="flex flex-col items-center space-y-4">
            <span className="text-xl text-foreground font-medium">Case Studies</span>
            <Link 
              to="/case-studies/b2b-lending" 
              className="text-lg text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              B2B Lending
            </Link>
            <Link 
              to="/case-studies/healthcare" 
              className="text-lg text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Healthcare
            </Link>
            <Link 
              to="/case-studies/service-business" 
              className="text-lg text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Service Businesses
            </Link>
          </div>
          
          <a 
            href="#contact" 
            className="primary-button text-xl"
            onClick={() => setIsMenuOpen(false)}
          >
            Get Started
          </a>
        </div>
      </div>
    </header>
  );
};
